import moment from "moment";

/**
 * Generates an HTML string of the booking confirmation document.
 * Used by both BookingPrint (download) and ComposeMail (PDF attachment).
 *
 * @param {object} params
 * @param {object} params.formData       - Booking data from getBookingDetails
 * @param {string} params.customerAddress - Formatted customer address string
 * @param {object} params.MasterCompanyDetails - Company profile from redux
 * @returns {string} Full HTML string ready for pdfblobURL()
 */
export const BookingPrintHTML = ({ formData, customerAddress, MasterCompanyDetails }) => {
    if (!formData) return "";

    const formatDate = (dateString) => {
        if (!dateString) return "";
        return moment(dateString).isValid()
            ? moment(dateString).format("DD MMM YYYY")
            : "";
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return "";
        return moment(dateString).isValid()
            ? moment(dateString).format("DD MMM YYYY HH:mm")
            : "";
    };

    const safeText = (val) => (val ? String(val).split("[")[0].trim() : "");

    // Process rates from formData (AR = arCharges, AP = apCharges)
    const ar = Array.isArray(formData.arCharges) ? formData.arCharges : [];
    const ap = Array.isArray(formData.apCharges)
        ? formData.apCharges.filter((i) => Number(i.amount) > 0)
        : [];

    const normalizedAR = ar.map((item) => ({
        type: "AR",
        description: item.description || "",
        basis: item.basis || "",
        quantity: Number(item.quantity || 0),
        container: item.container?.trim() || "GENERAL",
        rate: Number(item.sellRate || 0),
        amount: Number(item.amount || 0),
    }));

    const normalizedAP = ap.map((item) => ({
        type: "AP",
        description: item.description || "",
        basis: item.basis || "",
        quantity: Number(item.quantity || 0),
        container: item.container?.trim() || "GENERAL",
        rate: Number(item.buyRate || 0),
        amount: Number(item.amount || 0),
    }));

    const allCharges = [...normalizedAR, ...normalizedAP];
    const containerTypes = [...new Set(allCharges.map((i) => i.container))];

    const grouped = {};
    containerTypes.forEach((cont) => {
        grouped[cont] = {
            AR: allCharges.filter((i) => i.container === cont && i.type === "AR"),
            AP: allCharges.filter((i) => i.container === cont && i.type === "AP"),
        };
    });

    let totalArAll = 0;
    let totalApAll = 0;
    containerTypes.forEach((ct) => {
        totalArAll += (grouped[ct]?.AR || []).reduce((s, i) => s + i.amount, 0);
        totalApAll += (grouped[ct]?.AP || []).reduce((s, i) => s + i.amount, 0);
    });
    const totalProfit = totalArAll - totalApAll;

    const fmt = (n) =>
        Number(n).toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });

    const companyAddressLines = (MasterCompanyDetails?.[0]?.ProfileAddressPrint || "")
        .split(",")
        .map((l) => `<p style="margin:0;font-size:11px;color:#555;">${l.trim()}</p>`)
        .join("");

    const customerAddressBlock = customerAddress
        ? customerAddress
              .split("\r\n")
              .map((l) => `<p style="margin:0;font-size:11px;color:#444;">${l}</p>`)
              .join("")
        : `
        <p style="margin:0;font-size:11px;color:#444;">${formData.customerName || ""}</p>
        <p style="margin:0;font-size:11px;color:#444;">Contact: ${formData.contactName || ""}</p>
        <p style="margin:0;font-size:11px;color:#444;">TEL: ${formData.telephone || ""}</p>
        <p style="margin:0;font-size:11px;color:#444;">Email: ${formData.email || ""}</p>
    `;

    const cargoDimRows = (formData.cargoDimensionDetails || [])
        .filter((item) => Number(item?.piece) > 0)
        .map(
            (item) => `
        <tr>
            <td style="font-size:11px;color:#444;padding:2px 0;">
                ${item.piece} ${(item.packageType || "").toUpperCase()}(S) /
                ${Number(item.length).toFixed(2)} X ${Number(item.width).toFixed(2)} X ${Number(item.height).toFixed(2)}
                ${item.dimensionType} /
                ${Number(item.weightKgs).toFixed(2)} KGS /
                ${Number(item.cbm).toFixed(2)} CBM
            </td>
        </tr>
    `
        )
        .join("");

    const chargesSectionHTML = containerTypes
        .map((containerType, idx) => {
            const arList = grouped[containerType]?.AR || [];
            const apList = grouped[containerType]?.AP || [];
            if (arList.length === 0 && apList.length === 0) return "";

            const arTotal = arList.reduce((s, i) => s + i.amount, 0);
            const apTotal = apList.reduce((s, i) => s + i.amount, 0);

            const arRows = arList
                .map(
                    (item) => `
                <tr>
                    <td style="padding:4px 8px;border-bottom:1px solid #eee;font-size:11px;">${item.description || "-"}</td>
                    <td style="padding:4px 8px;border-bottom:1px solid #eee;font-size:11px;">${item.basis || "-"}</td>
                    <td style="padding:4px 8px;border-bottom:1px solid #eee;font-size:11px;text-align:right;">${item.quantity?.toLocaleString() || "0"}</td>
                    <td style="padding:4px 8px;border-bottom:1px solid #eee;font-size:11px;">${item.container === "GENERAL" ? "-" : item.container}</td>
                    <td style="padding:4px 8px;border-bottom:1px solid #eee;font-size:11px;text-align:right;">${fmt(item.rate)}</td>
                    <td style="padding:4px 8px;border-bottom:1px solid #eee;font-size:11px;text-align:right;font-weight:600;">${fmt(item.amount)}</td>
                </tr>
            `
                )
                .join("");

            const apRows = apList
                .map(
                    (item) => `
                <tr>
                    <td style="padding:4px 8px;border-bottom:1px solid #eee;font-size:11px;">${item.description || "-"}</td>
                    <td style="padding:4px 8px;border-bottom:1px solid #eee;font-size:11px;">${item.basis || "-"}</td>
                    <td style="padding:4px 8px;border-bottom:1px solid #eee;font-size:11px;text-align:right;">${item.quantity?.toLocaleString() || "0"}</td>
                    <td style="padding:4px 8px;border-bottom:1px solid #eee;font-size:11px;">${item.container === "GENERAL" ? "-" : item.container}</td>
                    <td style="padding:4px 8px;border-bottom:1px solid #eee;font-size:11px;text-align:right;">${fmt(item.rate)}</td>
                    <td style="padding:4px 8px;border-bottom:1px solid #eee;font-size:11px;text-align:right;font-weight:600;">${fmt(item.amount)}</td>
                </tr>
            `
                )
                .join("");

            return `
            ${containerType !== "GENERAL" ? `<div style="background:#f3f4f6;padding:4px 8px;border-bottom:1px solid #ddd;font-weight:600;font-size:12px;">Container: ${containerType}</div>` : ""}
            ${arList.length > 0 ? `
                <table style="width:100%;border-collapse:collapse;">
                    <thead>
                        <tr style="background:#f0fdf4;">
                            <th colspan="6" style="text-align:left;padding:6px 8px;font-size:11px;color:#166534;border-bottom:1px solid #bbf7d0;">AR Charges (Sell)</th>
                        </tr>
                        <tr style="background:#f9fafb;">
                            <th style="text-align:left;padding:4px 8px;font-size:11px;color:#374151;border-bottom:1px solid #e5e7eb;width:35%;">Description</th>
                            <th style="text-align:left;padding:4px 8px;font-size:11px;color:#374151;border-bottom:1px solid #e5e7eb;">Basis</th>
                            <th style="text-align:right;padding:4px 8px;font-size:11px;color:#374151;border-bottom:1px solid #e5e7eb;">Qty</th>
                            <th style="text-align:left;padding:4px 8px;font-size:11px;color:#374151;border-bottom:1px solid #e5e7eb;">Container</th>
                            <th style="text-align:right;padding:4px 8px;font-size:11px;color:#374151;border-bottom:1px solid #e5e7eb;">Rate</th>
                            <th style="text-align:right;padding:4px 8px;font-size:11px;color:#374151;border-bottom:1px solid #e5e7eb;">Amount</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${arRows}
                        <tr style="background:#f0fdf4;">
                            <td colspan="5" style="padding:6px 8px;text-align:right;font-weight:600;font-size:11px;color:#166534;">Subtotal:</td>
                            <td style="padding:6px 8px;text-align:right;font-weight:700;font-size:11px;color:#166534;">${fmt(arTotal)}</td>
                        </tr>
                    </tbody>
                </table>
            ` : ""}
            ${arList.length > 0 && apList.length > 0 ? '<div style="height:6px;"></div>' : ""}
            ${apList.length > 0 ? `
                <table style="width:100%;border-collapse:collapse;">
                    <thead>
                        <tr style="background:#fef2f2;">
                            <th colspan="6" style="text-align:left;padding:6px 8px;font-size:11px;color:#991b1b;border-bottom:1px solid #fecaca;">AP Charges (Buy)</th>
                        </tr>
                        <tr style="background:#f9fafb;">
                            <th style="text-align:left;padding:4px 8px;font-size:11px;color:#374151;border-bottom:1px solid #e5e7eb;width:35%;">Description</th>
                            <th style="text-align:left;padding:4px 8px;font-size:11px;color:#374151;border-bottom:1px solid #e5e7eb;">Basis</th>
                            <th style="text-align:right;padding:4px 8px;font-size:11px;color:#374151;border-bottom:1px solid #e5e7eb;">Qty</th>
                            <th style="text-align:left;padding:4px 8px;font-size:11px;color:#374151;border-bottom:1px solid #e5e7eb;">Container</th>
                            <th style="text-align:right;padding:4px 8px;font-size:11px;color:#374151;border-bottom:1px solid #e5e7eb;">Rate</th>
                            <th style="text-align:right;padding:4px 8px;font-size:11px;color:#374151;border-bottom:1px solid #e5e7eb;">Amount</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${apRows}
                        <tr style="background:#fef2f2;">
                            <td colspan="5" style="padding:6px 8px;text-align:right;font-weight:600;font-size:11px;color:#991b1b;">Subtotal:</td>
                            <td style="padding:6px 8px;text-align:right;font-weight:700;font-size:11px;color:#991b1b;">${fmt(apTotal)}</td>
                        </tr>
                    </tbody>
                </table>
            ` : ""}
            ${idx < containerTypes.length - 1 ? '<div style="border-top:1px solid #e5e7eb;margin:8px 0;"></div>' : ""}
        `;
        })
        .join("");

    const globalTotalsHTML =
        totalArAll > 0 || totalApAll > 0
            ? `
        <div style="padding:10px 16px;border-top:2px solid #e5e7eb;background:${totalProfit >= 0 ? "#eff6ff" : "#fff7ed"};display:flex;justify-content:flex-end;gap:24px;">
            <div style="text-align:right;">
                <div style="font-size:11px;color:#374151;font-weight:600;">Total AR (Sell):</div>
                <div style="font-size:15px;font-weight:700;color:#16a34a;">${fmt(totalArAll)}</div>
            </div>
            <div style="text-align:right;">
                <div style="font-size:11px;color:#374151;font-weight:600;">Total AP (Buy):</div>
                <div style="font-size:15px;font-weight:700;color:#dc2626;">${fmt(totalApAll)}</div>
            </div>
            <div style="text-align:right;padding-left:16px;border-left:2px solid #d1d5db;">
                <div style="font-size:11px;color:#374151;font-weight:600;">Total Profit/Loss:</div>
                <div style="font-size:17px;font-weight:700;color:${totalProfit >= 0 ? "#16a34a" : "#dc2626"};">${fmt(totalProfit)}</div>
            </div>
        </div>
    `
            : "";

    return `
    <!DOCTYPE html>
    <html>
    <head>
        <meta charset="UTF-8"/>
        <style>
            * { box-sizing: border-box; margin: 0; padding: 0; }
            body { font-family: Arial, sans-serif; background: white; color: #111; }
            .section-header { background: #1a2744; color: white; padding: 6px 12px; }
            .section-header h3 { font-size: 12px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; }
            table { border-collapse: collapse; }
            td, th { vertical-align: top; }
        </style>
    </head>
    <body>
    <div style="width:100%;background:white;padding:16px;">

        <!-- Header -->
        <div style="display:flex;justify-content:space-between;align-items:flex-start;padding-bottom:12px;border-bottom:1px solid #e5e7eb;margin-bottom:0;">
            <div>
                <img src="/shipez-logo.png" alt="Logo" style="height:40px;width:auto;" />
                <p style="font-size:10px;color:#6b7280;margin-top:2px;">Intelligent Freight Solutions</p>
            </div>
            <div style="text-align:right;">${companyAddressLines}</div>
        </div>

        <!-- Booking Confirmation -->
        <div style="border:1px solid #9ca3af;margin-top:0;">
            <div class="section-header"><h3>Booking Confirmation</h3></div>
            <div style="display:grid;grid-template-columns:1fr 1fr;">
                <div style="padding:10px;font-size:11px;">${customerAddressBlock}</div>
                <div style="padding:10px;border-left:1px solid #9ca3af;">
                    <table style="width:100%;font-size:11px;">
                        <tr><td style="color:#555;font-weight:600;width:130px;">BOOKING NO.</td><td style="color:#111;">: ${formData.carrierBookingNumber || ""}</td></tr>
                        <tr><td style="color:#555;font-weight:600;">FILE No.</td><td style="color:#111;">: ${formData.fileNumber || ""}</td></tr>
                        <tr><td style="color:#555;font-weight:600;">CREATED DATE</td><td style="color:#111;">: ${formatDateTime(formData.createdOn)}</td></tr>
                        <tr><td style="color:#555;font-weight:600;">SHIPPING TYPE</td><td style="color:#111;">: ${formData.shippingType || ""}</td></tr>
                        <tr><td style="color:#555;font-weight:600;">OPERATOR</td><td style="color:#111;">: ${formData.operatorName || ""}</td></tr>
                    </table>
                </div>
            </div>
        </div>

        <!-- Routing & Vessel -->
        <div style="border:1px solid #9ca3af;border-top:none;">
            <div class="section-header"><h3>Routing &amp; Vessel Details</h3></div>
            <div style="display:grid;grid-template-columns:1fr 1fr;">
                <div style="padding:8px;">
                    <table style="width:100%;font-size:11px;">
                        <tr><td style="color:#555;font-weight:600;width:120px;padding:2px 0;">CARRIER</td><td style="color:#111;padding:2px 0;">: ${formData.carrier || ""}</td></tr>
                        <tr><td style="color:#555;font-weight:600;padding:2px 0;">VESSEL/VOYAGE</td><td style="color:#111;padding:2px 0;">: ${formData.vessel || ""}/${formData.voyage || ""}</td></tr>
                        <tr><td style="color:#555;font-weight:600;padding:2px 0;">MOVE TYPE</td><td style="color:#111;padding:2px 0;">: ${safeText(formData.ourMoveType)}</td></tr>
                        <tr><td style="color:#555;font-weight:600;padding:2px 0;">DOOR/ORIGIN/ZIP</td><td style="color:#111;padding:2px 0;">: ${safeText(formData.ourPlaceOfReceipt)}</td></tr>
                        <tr><td style="color:#555;font-weight:600;padding:2px 0;">PORT OF LOAD</td><td style="color:#111;padding:2px 0;">: ${safeText(formData.portOfLoading)}</td></tr>
                        <tr><td style="color:#555;font-weight:600;padding:2px 0;">TRANSHIPMENT PORT</td><td style="color:#111;padding:2px 0;">: ${safeText(formData.transhipmentPorts?.[0]?.transPortCity)}</td></tr>
                        <tr><td style="color:#555;font-weight:600;padding:2px 0;">PORT OF DISCHARGE</td><td style="color:#111;padding:2px 0;">: ${safeText(formData.portOfDischarge)}</td></tr>
                        <tr><td style="color:#555;font-weight:600;padding:2px 0;">FINAL DESTINATION</td><td style="color:#111;padding:2px 0;">: ${safeText(formData.ourPlaceOfDelivery)}</td></tr>
                    </table>
                </div>
                <div style="padding:8px;border-left:1px solid #9ca3af;">
                    <table style="width:100%;font-size:11px;">
                        <tr><td style="color:#555;font-weight:600;width:140px;padding:2px 0;">EARLIEST RETURN DATE</td><td style="color:#111;padding:2px 0;">: ${formatDateTime(formData.earliestRecvdDate)}</td></tr>
                        <tr><td style="color:#555;font-weight:600;padding:2px 0;">DOC CUTOFF</td><td style="color:#111;padding:2px 0;">: ${formatDateTime(formData.ourDocCo)}</td></tr>
                        <tr><td style="color:#555;font-weight:600;padding:2px 0;">VGM CUTOFF</td><td style="color:#111;padding:2px 0;">: ${formatDateTime(formData.vgmCo)}</td></tr>
                        <tr><td style="color:#555;font-weight:600;padding:2px 0;">RAIL CUTOFF</td><td style="color:#111;padding:2px 0;">: ${formatDateTime(formData.inlandEailCo)}</td></tr>
                        <tr><td style="color:#555;font-weight:600;padding:2px 0;">AUTO CUTOFF</td><td style="color:#111;padding:2px 0;">: ${formatDateTime(formData.autoCo)}</td></tr>
                        <tr><td style="color:#555;font-weight:600;padding:2px 0;">PORT CUTOFF</td><td style="color:#111;padding:2px 0;">: ${formatDateTime(formData.portCo)}</td></tr>
                        <tr><td style="color:#555;font-weight:600;padding:2px 0;">ETD POL</td><td style="color:#111;padding:2px 0;">: ${formatDateTime(formData.etsAtPol)}</td></tr>
                        <tr><td style="color:#555;font-weight:600;padding:2px 0;">ETA POD</td><td style="color:#111;padding:2px 0;">: ${formatDateTime(formData.etaAtPod)}</td></tr>
                        <tr><td style="color:#555;font-weight:600;padding:2px 0;">ETA DESTINATION</td><td style="color:#111;padding:2px 0;">: ${formatDateTime(formData.etaAtDestination)}</td></tr>
                    </table>
                </div>
            </div>
        </div>

        <!-- Cargo Details -->
        <div style="border:1px solid #9ca3af;border-top:none;">
            <div class="section-header"><h3>Cargo Details</h3></div>
            <div style="display:grid;grid-template-columns:1fr 1fr;">
                <div style="padding:10px;border-right:1px solid #9ca3af;">
                    <table style="width:100%;">${cargoDimRows}</table>
                </div>
                <div style="padding:10px;font-size:11px;color:#444;">${formData.commodity || ""}</div>
            </div>
        </div>

        <!-- Equipment -->
        <div style="border:1px solid #9ca3af;border-top:none;">
            <div class="section-header"><h3>Equipment</h3></div>
            <div style="display:grid;grid-template-columns:1fr 1fr;">
                <div style="padding:8px;font-size:11px;color:#111;">${formData.containerCount}x${formData.containerType || ""}</div>
                <div style="padding:8px;border-left:1px solid #9ca3af;">
                    <table style="width:100%;font-size:11px;">
                        <tr><td style="color:#555;font-weight:600;width:50%;padding:2px 0;">HAZARDOUS</td><td style="color:#111;padding:2px 0;">: ${formData.hazCargo ? "Yes" : "No"}</td></tr>
                        <tr><td style="color:#555;font-weight:600;padding:2px 0;">IMO NO</td><td style="color:#111;padding:2px 0;">: ${formData.hazImoClass || ""}</td></tr>
                        <tr><td style="color:#555;font-weight:600;padding:2px 0;">PKG</td><td style="color:#111;padding:2px 0;">: ${formData.hazPkg || ""}</td></tr>
                        <tr><td style="color:#555;font-weight:600;padding:2px 0;">UN NBR</td><td style="color:#111;padding:2px 0;">: ${formData.hazUnNbr || ""}</td></tr>
                    </table>
                </div>
            </div>
        </div>

        <!-- Domestic Routing -->
        <div style="border:1px solid #9ca3af;border-top:none;">
            <div class="section-header"><h3>Domestic Routing</h3></div>
            <div style="display:grid;grid-template-columns:1fr 1fr;border-bottom:1px solid #9ca3af;">
                <div style="padding:8px;">
                    <table style="width:100%;font-size:11px;">
                        <tr><td style="color:#555;font-weight:600;width:140px;padding:2px 0;">PICKUP TERMINAL</td><td style="color:#111;padding:2px 0;">: ${safeText(formData.carrierPlaceOfReceipt)}</td></tr>
                        <tr><td style="color:#555;font-weight:600;padding:2px 0;">EARLIEST PICK-UP DATE</td><td style="color:#111;padding:2px 0;">: ${formatDate(formData.earliestPickupDate)}</td></tr>
                    </table>
                </div>
                <div style="padding:8px;border-left:1px solid #9ca3af;">
                    <table style="width:100%;font-size:11px;">
                        <tr><td style="color:#555;font-weight:600;width:140px;padding:2px 0;">DELIVERY TERMINAL</td><td style="color:#111;padding:2px 0;">: ${formData.carrierPlaceOfDelivery || ""}</td></tr>
                        <tr><td style="color:#555;font-weight:600;padding:2px 0;">EARLIEST RETURN DATE</td><td style="color:#111;padding:2px 0;">: ${formatDate(formData.truckerLatestDeliveryDate)}</td></tr>
                    </table>
                </div>
            </div>
            <div style="display:grid;grid-template-columns:1fr 1fr;">
                <div style="padding:8px;">
                    <table style="width:100%;font-size:11px;">
                        <tr><td style="color:#555;font-weight:600;width:140px;padding:2px 0;">LOADING LOCATION</td><td style="color:#111;padding:2px 0;">: ${formData.loadingLocationWarehouse || ""}</td></tr>
                        <tr><td style="color:#555;font-weight:600;padding:2px 0;">LIVE LOAD</td><td style="color:#111;padding:2px 0;">: ${formData.loadingStatus || ""}</td></tr>
                        <tr><td style="color:#555;font-weight:600;padding:2px 0;">DROP DATE</td><td style="color:#111;padding:2px 0;">: ${formatDate(formData.dropDate)}</td></tr>
                        <tr><td style="color:#555;font-weight:600;padding:2px 0;">PICKUP DATE</td><td style="color:#111;padding:2px 0;">: ${formatDate(formData.pickupDate)}</td></tr>
                    </table>
                </div>
                <div style="padding:8px;border-left:1px solid #9ca3af;">
                    <table style="width:100%;font-size:11px;">
                        <tr><td style="color:#555;font-weight:600;width:140px;padding:2px 0;">TRUCKER/INLAND CARRIER</td><td style="color:#111;padding:2px 0;">: ${formData.trucker || ""}</td></tr>
                        <tr><td style="color:#555;font-weight:600;padding:2px 0;">CTC TRUCKER</td><td style="color:#111;padding:2px 0;">: ${formData.truckerPoc || ""}</td></tr>
                    </table>
                </div>
            </div>
        </div>

        <!-- Charges Summary -->
        <div style="border:1px solid #9ca3af;border-top:none;">
            <div class="section-header"><h3>Charges Summary</h3></div>
            ${chargesSectionHTML || '<div style="padding:16px;text-align:center;color:#6b7280;font-size:12px;">No charges data available</div>'}
            ${globalTotalsHTML}
        </div>

        <!-- Footer -->
        <div style="border-top:1px solid #e5e7eb;padding-top:12px;margin-top:16px;">
            <div style="display:grid;grid-template-columns:1fr 1fr;">
                <div>
                    <p style="font-size:10px;color:#6b7280;">Prepared By:</p>
                    <p style="font-size:13px;font-weight:600;color:#111;">${formData.createdBy || ""}</p>
                    <p style="font-size:11px;color:#444;">Shipez AI - ${formData.officeType || ""}</p>
                    <p style="font-size:10px;color:#6b7280;margin-top:4px;">Date: ${formatDateTime(formData.createdOn)}</p>
                </div>
                <div style="text-align:right;">
                    <p style="font-size:10px;color:#6b7280;">Customer Acceptance:</p>
                    <div style="border-bottom:1px solid #9ca3af;width:160px;height:24px;margin-left:auto;margin-bottom:4px;"></div>
                    <p style="font-size:10px;color:#6b7280;">Authorized Signature &amp; Date</p>
                </div>
            </div>
            <div style="margin-top:12px;padding-top:8px;border-top:1px solid #e5e7eb;text-align:center;">
                <p style="font-size:10px;color:#9ca3af;">
                    This is a computer generated document. No signature required. | Thank you for choosing Shipez AI | www.shipez.ai
                </p>
            </div>
        </div>

    </div>
    </body>
    </html>
    `;
};